// Pure decision logic for the one lifecycle card the dashboard shows at a
// time. No React and no platform calls here so it can be unit tested;
// useLifecycleCard.ts gathers the inputs and wires the actions.

export type LifecycleCardId = 'rescue' | 'year-in-doqit' | 'three-things' | 'notifications-day-2' | 'two-more' | 'next-up';

export type LifecycleTier = 'engaged' | 'light' | 'none';

export type LifecyclePeriodType = 'TRIAL' | 'NORMAL' | 'INTRO' | 'PREPAID';

export interface LifecycleEntitlement {
  isActive: boolean;
  // Null when the platform did not say (treated as "unknown", never as off).
  willRenew: boolean | null;
  // ISO timestamps.
  expirationDate: string | null;
  periodType: LifecyclePeriodType | null;
  productIdentifier: string | null;
  // Google Play base plan id (e.g. "annual"), when there is one.
  productPlanIdentifier?: string | null;
  latestPurchaseDate: string | null;
  // Upper-cased store name (e.g. APP_STORE, PROMOTIONAL).
  store?: string | null;
  // ISO timestamps; set when the user turned renewal off / billing failed.
  unsubscribeDetectedAt?: string | null;
  billingIssueDetectedAt?: string | null;
}

export interface LifecycleNextReminder {
  docName: string;
  // UTC-midnight Date for the reminder's calendar day.
  reminderDate: Date;
  // Null when the API row carried no document id: next-up then falls back to
  // its "add something" copy rather than linking nowhere.
  documentId: number | null;
}

// Which button the rescue card carries. The backend decides eligibility
// (it has the real engagement numbers), so the card asks it:
// 'rescue' = the £19.99 offer, 'monthly' = go month by month, 'none' = no
// price at all, 'pending' = the checks are still running (no price button
// yet, so a price never flickers in and then disappears).
export type RescueOffer = 'rescue' | 'monthly' | 'none' | 'pending';

export type LifecyclePermission = 'granted' | 'prompt' | 'denied' | 'unsupported';

export interface LifecycleCardInput {
  entitlement: LifecycleEntitlement | null;
  docCount: number;
  hasDatedReminder: boolean;
  nextReminder: LifecycleNextReminder | null;
  hasBoundedReminderCard: boolean;
  now: Date;
  // Card id -> ISO time the dismissal runs out.
  dismissedUntil: Partial<Record<LifecycleCardId, string>>;
  seen: { threeThings: boolean };
  previouslyTrial: boolean;
  notificationPermission: LifecyclePermission;
  // Backend answer for the rescue card's button. Missing means 'pending'.
  rescueOffer?: RescueOffer;
  // ISO time the account was made. Unknown means the "two more" nudge,
  // which is only for someone's first week, stays hidden.
  accountCreatedAt?: string | null;
}

export type LifecycleCardAction =
  | { kind: 'navigate'; to: string }
  | { kind: 'upload' }
  // Upload, straight into the camera on a phone.
  | { kind: 'camera' }
  | { kind: 'primer' };

export interface LifecycleCardButton {
  // Short stable name for analytics.
  id: string;
  label: string;
  variant: 'primary' | 'secondary';
  action: LifecycleCardAction;
}

export interface LifecycleCardRow {
  id: string;
  label: string;
  to: string;
}

export interface LifecycleCardDescriptor {
  id: LifecycleCardId;
  tier: LifecycleTier;
  eyebrow?: string;
  title: string;
  body: string;
  buttons: LifecycleCardButton[];
  rows?: LifecycleCardRow[];
  // Whole-card tap target, for cards without buttons.
  tap?: LifecycleCardAction;
  dismissible: boolean;
}

// Only the fields the count needs, so the raw API rows and the merged
// dashboard rows both fit.
export interface CountableCategory {
  documents_count?: number | null;
  own_documents_count?: number | null;
  is_shared_in?: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1000;
export const DISMISS_DAYS = 3;
// The "two more" nudge rests for 2 days after a dismissal, not 3.
export const DISMISS_DAYS_BY_CARD: Partial<Record<LifecycleCardId, number>> = { 'two-more': 2 };
const FIRST_WEEK_DAYS = 7;
export const ACTIVATION_TARGET_DOCS = 3;
const YEAR_WINDOW_DAYS = 30;

/**
 * The user's OWN documents across the dashboard categories. A shared-in row's
 * documents_count is the other person's shared documents, so for those rows
 * only own_documents_count counts. The merged rows (mergedCategoryData) have
 * already copied own_documents_count into documents_count but keep
 * is_shared_in, so this gives the same answer for either list.
 */
export const getOwnDocumentCount = (categories: CountableCategory[] | null | undefined): number => (
  (categories || []).reduce((sum, cat) => {
    const own = cat.is_shared_in ? cat.own_documents_count : cat.documents_count;
    return sum + (Number.isFinite(own) ? Number(own) : 0);
  }, 0)
);

export const computeTier = ({ docCount, hasDatedReminder }: { docCount: number; hasDatedReminder: boolean }): LifecycleTier => {
  if (docCount >= 2 || (docCount === 1 && hasDatedReminder)) return 'engaged';
  if (docCount === 1) return 'light';
  return 'none';
};

// No product ids live in this repo (they are configured in RevenueCat and the
// stores), so annual is detected by name. Google Play reports the base plan
// separately, so both ids are checked.
export const isAnnualProduct = (entitlement: Pick<LifecycleEntitlement, 'productIdentifier' | 'productPlanIdentifier'> | null): boolean => {
  if (!entitlement) return false;
  const ids = `${entitlement.productIdentifier || ''} ${entitlement.productPlanIdentifier || ''}`;
  return /annual|year/i.test(ids);
};

const toTime = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

export const isDismissed = (
  id: LifecycleCardId,
  dismissedUntil: LifecycleCardInput['dismissedUntil'],
  now: Date,
): boolean => {
  const until = toTime(dismissedUntil[id]);
  return until !== null && now.getTime() < until;
};

export const dismissUntilIso = (now: Date, id?: LifecycleCardId): string => {
  const days = (id && DISMISS_DAYS_BY_CARD[id]) ?? DISMISS_DAYS;
  return new Date(now.getTime() + days * DAY_MS).toISOString();
};

// Expiry is a real moment, so it is shown in the phone's own timezone.
export const formatDayMonth = (date: Date): string => new Intl.DateTimeFormat('en-GB', {
  day: 'numeric', month: 'long',
}).format(date);

// Reminder dates arrive as calendar days (UTC midnight), so format in UTC or
// the day drifts west of Greenwich.
export const formatCalendarDay = (date: Date): string => new Intl.DateTimeFormat('en-GB', {
  day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
}).format(date);

const UPLOAD: LifecycleCardAction = { kind: 'upload' };

/**
 * Turns the backend eligibility answers into the rescue card's offer.
 * null = that check is still in flight. Monthly is only asked about (and
 * only counts) for someone on an annual plan.
 */
export const chooseRescueOffer = ({ rescueEligible, monthlyEligible, onAnnual }: {
  rescueEligible: boolean | null;
  monthlyEligible: boolean | null;
  onAnnual: boolean;
}): RescueOffer => {
  if (rescueEligible === null) return 'pending';
  if (rescueEligible) return 'rescue';
  if (!onAnnual) return 'none';
  if (monthlyEligible === null) return 'pending';
  return monthlyEligible ? 'monthly' : 'none';
};

const rescueCard = (
  entitlement: LifecycleEntitlement,
  tier: LifecycleTier,
  offer: RescueOffer,
): LifecycleCardDescriptor => {
  const expiry = toTime(entitlement.expirationDate);
  const until = expiry !== null ? formatDayMonth(new Date(expiry)) : 'the end of your plan';
  let buttons: LifecycleCardButton[];
  if (offer === 'pending') {
    buttons = [];
  } else if (offer === 'rescue') {
    buttons = [{
      id: 'offer_rescue', label: 'Keep Doqit for £19.99 a year', variant: 'primary', action: { kind: 'navigate', to: '/offer/rescue' },
    }];
  } else if (offer === 'monthly') {
    buttons = [{
      id: 'offer_monthly', label: 'Go month by month', variant: 'primary', action: { kind: 'navigate', to: '/offer/monthly' },
    }];
  } else if (tier === 'none') {
    buttons = [{
      id: 'add_first_document', label: 'Add your first document', variant: 'primary', action: UPLOAD,
    }];
  } else {
    buttons = [{
      id: 'add_document', label: 'Add another document', variant: 'primary', action: UPLOAD,
    }];
  }
  return {
    id: 'rescue',
    tier,
    title: "You've switched renewal off",
    body: `Nothing changes until ${until}. Your documents and reminders stay put until then.`,
    buttons,
    dismissible: true,
  };
};

export const isRescueCandidate = (entitlement: LifecycleEntitlement): boolean => (
  entitlement.isActive
  && entitlement.willRenew === false
  && entitlement.unsubscribeDetectedAt != null
  && entitlement.store !== 'PROMOTIONAL'
  && entitlement.billingIssueDetectedAt == null
  && entitlement.expirationDate != null
);

export const pickDashboardCard = (input: LifecycleCardInput): LifecycleCardDescriptor | null => {
  const {
    entitlement, docCount, hasDatedReminder, nextReminder, hasBoundedReminderCard, now,
    dismissedUntil, seen, previouslyTrial, notificationPermission, rescueOffer = 'pending', accountCreatedAt,
  } = input;
  const tier = computeTier({ docCount, hasDatedReminder });
  const nowMs = now.getTime();
  const active = !!entitlement?.isActive;
  const hidden = (id: LifecycleCardId) => isDismissed(id, dismissedUntil, now);

  // 1. Rescue: the user switched renewal off on a live store plan. willRenew
  // is also false for comped/promotional access, billing-issue grace periods
  // and non-renewing products, so require an actual unsubscribe.
  if (entitlement && isRescueCandidate(entitlement) && !hidden('rescue')) {
    return rescueCard(entitlement, tier, rescueOffer);
  }

  // 2. Year in Doqit: annual plan renewing within 30 days.
  if (entitlement && active && entitlement.willRenew === true && entitlement.periodType === 'NORMAL'
    && isAnnualProduct(entitlement) && !hidden('year-in-doqit')) {
    const expiry = toTime(entitlement.expirationDate);
    if (expiry !== null && expiry > nowMs && expiry - nowMs <= YEAR_WINDOW_DAYS * DAY_MS) {
      return {
        id: 'year-in-doqit',
        tier,
        title: 'Your year in Doqit',
        body: `${docCount} ${docCount === 1 ? 'document' : 'documents'} kept. Here's what's coming up next year.`,
        buttons: [],
        tap: { kind: 'navigate', to: '/reminders' },
        dismissible: true,
      };
    }
  }

  // 3. Three things: first paid period after a trial, shown until seen.
  if (entitlement && active && entitlement.periodType === 'NORMAL' && previouslyTrial && !seen.threeThings) {
    return {
      id: 'three-things',
      tier,
      title: "Three things you haven't tried",
      body: '',
      buttons: [],
      rows: [
        { id: 'save_from_anywhere', label: 'Save from any app', to: '/learn/save-from-anywhere' },
        { id: 'find_it_in_seconds', label: 'Find anything in seconds', to: '/learn/find-it-in-seconds' },
        { id: 'never_miss_a_renewal', label: 'Let Doqit remember the dates', to: '/learn/never-miss-a-renewal' },
      ],
      dismissible: true,
    };
  }

  // 4. Notifications, day 2 of the trial.
  const purchased = toTime(entitlement?.latestPurchaseDate);
  if (entitlement && active && entitlement.periodType === 'TRIAL' && purchased !== null
    && nowMs - purchased >= DAY_MS && notificationPermission === 'prompt' && !hidden('notifications-day-2')) {
    return {
      id: 'notifications-day-2',
      tier,
      title: 'Get your reminders on time',
      body: 'Allow notifications so Doqit can tell you before something runs out.',
      buttons: [{
        id: 'turn_on_notifications', label: 'Turn on notifications', variant: 'primary', action: { kind: 'primer' },
      }],
      dismissible: true,
    };
  }

  // 5. Two more to go: one or two documents in the first week. Gone at three.
  const created = toTime(accountCreatedAt);
  if (docCount >= 1 && docCount < ACTIVATION_TARGET_DOCS && created !== null
    && nowMs >= created && nowMs - created < FIRST_WEEK_DAYS * DAY_MS && !hidden('two-more')) {
    return {
      id: 'two-more',
      tier,
      title: docCount === 1 ? 'One in. Two more and Doqit starts earning its keep.' : 'Two in. One more and Doqit starts earning its keep.',
      body: 'Most people who stay have at least three.',
      buttons: [{
        id: 'add_document', label: 'Add', variant: 'primary', action: { kind: 'camera' },
      }],
      dismissible: true,
    };
  }

  // 6. Next up: only when the bounded YOUR REMINDERS card is not showing.
  if (hasBoundedReminderCard) return null;
  if (nextReminder && nextReminder.documentId != null) {
    return {
      id: 'next-up',
      tier,
      eyebrow: 'NEXT UP',
      title: nextReminder.docName,
      body: `Doqit will remind you on ${formatCalendarDay(nextReminder.reminderDate)}.`,
      buttons: [],
      tap: { kind: 'navigate', to: `/editDocuments/${nextReminder.documentId}` },
      dismissible: false,
    };
  }
  return {
    id: 'next-up',
    tier,
    title: 'Add something with a date',
    body: 'Doqit reads the date and reminds you before it runs out.',
    buttons: [],
    tap: UPLOAD,
    dismissible: false,
  };
};
